// Export hooks. The server generates the file, uploads to R2, and returns a
// short-lived presigned URL. We fetch on click (not on mount) so the user
// always gets a URL inside the 1-hour TTL window — useReusableExport just
// triggers the request lazily and tracks per-button loading state.

import { useMutation } from '@tanstack/react-query'
import { api } from '../api'

export type ExportFormat = 'pdf' | 'csv' | 'json'

export interface ExportResponse {
  url: string
  storage_key: string
  cached: boolean
  expires_in_sec: number
}

export interface CueSheetExportInput {
  project_id: string
  format?: 'csv' | 'json'
}
export interface ScriptBookExportInput {
  project_id: string
  view: 'by_scene' | 'by_character'
}
export interface QcReportExportInput {
  character_id: string
  version: number
}
export interface ProjectSummaryExportInput {
  project_id: string
}
export interface OneOhNineNineExportInput {
  actor_id: string
  tax_year: number
}
export interface ContractExportInput {
  contract_id: string
}

function qs(input: object): string {
  const parts: string[] = []
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null) continue
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
  }
  return parts.length ? `?${parts.join('&')}` : ''
}

export function useExportCueSheet() {
  return useMutation({
    mutationFn: (input: CueSheetExportInput) =>
      api<ExportResponse>(`/api/v1/exports/cue-sheet${qs(input)}`, { method: 'POST' }),
  })
}

export function useExportScriptBook() {
  return useMutation({
    mutationFn: (input: ScriptBookExportInput) =>
      api<ExportResponse>(`/api/v1/exports/script-book${qs(input)}`, { method: 'POST' }),
  })
}

export function useExportQcReport() {
  return useMutation({
    mutationFn: (input: QcReportExportInput) =>
      api<ExportResponse>(`/api/v1/exports/qc-report${qs(input)}`, { method: 'POST' }),
  })
}

export function useExportProjectSummary() {
  return useMutation({
    mutationFn: (input: ProjectSummaryExportInput) =>
      api<ExportResponse>(`/api/v1/exports/project-summary${qs(input)}`, { method: 'POST' }),
  })
}

export function useExport1099() {
  return useMutation({
    mutationFn: (input: OneOhNineNineExportInput) =>
      api<ExportResponse>(`/api/v1/exports/1099${qs(input)}`, { method: 'POST' }),
  })
}

export function useExportContract() {
  return useMutation({
    mutationFn: (input: ContractExportInput) =>
      api<ExportResponse>(`/api/v1/exports/contract${qs(input)}`, { method: 'POST' }),
  })
}

/**
 * Generic export endpoint trigger used by `ExportButtons`. Each call posts to
 * the supplied endpoint with the supplied params and resolves to the presigned
 * download URL.
 */
export async function triggerExport(
  endpoint: string,
  params: Record<string, string | number>,
): Promise<ExportResponse> {
  return api<ExportResponse>(`${endpoint}${qs(params)}`, { method: 'POST' })
}
